import Button, { ActionButton } from '@bugbug/core/components/Button';
import { useTranslation, Trans } from 'react-i18next';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { Test } from '@bugbug/core/types/tests';
import type { SideEffect } from '@bugbug/core/types/utils';
import { Header, Footer, ErrorInfo, DeleteIcon } from '~/components/modals/Modal';
import StepName from '~/components/StepName';
import useModal from '~/hooks/useModal';
import { useDeleteStep } from '~/modules/steps/hooks/useDeleteStep/useDeleteStep';
import { useAppSelector } from '~/modules/store';
import { selectStep } from '~/modules/tests/tests.selectors';
import { isInternalServerError } from '~/services/api/utils';

import * as S from './DeleteStepModal.styled';

interface DeleteStepModalProps {
  className?: string;
  testId: Test['id'];
  stepId: Step['id'];
  groupId: Group['id'];
  onSuccess?: SideEffect;
}

const DeleteStepModal = ({ testId, stepId, groupId, onSuccess }: DeleteStepModalProps) => {
  const { t } = useTranslation();
  const modal = useModal();
  const step = useAppSelector(selectStep(stepId, false));
  const [deleteStep, deleteStepState] = useDeleteStep();

  const handleDeleteConfirm = async () => {
    await deleteStep({ id: stepId, groupId, testId });

    modal.hide();
    onSuccess?.();
  };

  return (
    <S.Container>
      <S.Content>
        <DeleteIcon />
        <div>
          <Header>
            <Trans i18nKey="deleteStepModal.title" defaults="Delete step?" />
          </Header>
          <div>
            <Trans i18nKey="deleteStepModal.description">
              Are you sure you want to delete step "<StepName type={step?.type} />
              "?
            </Trans>
          </div>
        </div>
      </S.Content>
      <Footer>
        <ErrorInfo isVisible={isInternalServerError(deleteStepState)} />
        <Button onClick={modal.hide}>{t('deleteStepModal.cancel', 'Cancel')}</Button>
        <ActionButton
          onClick={handleDeleteConfirm}
          variant="primary"
          pending={deleteStepState.isLoading}
        >
          {t('deleteStepModal.confirm', 'Delete step')}
        </ActionButton>
      </Footer>
    </S.Container>
  );
};

export default DeleteStepModal;
